import { useState } from "react";
import { Card } from "@mui/material";

import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { InboundSaveButton } from "ui-components/Form";
import { InboundLayout } from "ui-components/Layout";
import { Success, Error_ } from "ui-components/Alert";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { FormTitle } from "ui-components/Form";
import MDBox from "components/MDBox";

import { headerRender, headerValues, headerValidation } from "./load-manifest-schema";
import LoadManifest from "./components/LoadManifest";
import { manifestColumns2 } from "./columns";

import { getLoadManifest } from "services/data.outbound.service";
import { editLoadManifest } from "services/data.outbound.service";
import { useForm } from "react-hook-form";
import { useCustomerValue } from "providers/CustomerContext";

const statusLabel = {
  L: "Open",
  D: "Delivered",
  C: "Cancel",
  A: "Allocate",
};

const statusObj = {
  Open: "L",
  Delivered: "D",
  Cancel: "C",
  Allocate: "A",
};

function EditLoadManifest() {
  const { customerCode } = useCustomerValue();
  const location = useLocation();
  const navigate = useNavigate();
  const { manifestNumber } = location.state;
  const [formValues, setFormValues] = useState({ ...headerValues, customerCode });
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });

  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
  } = form;

  const headerProps = {
    headerRender,
    customerCode,
    control,
    errors,
    mode: "EDIT",
  };

  useQuery({
    queryKey: ["manifest", manifestNumber],
    queryFn: () => getLoadManifest({ manifestNumber }),
    onSuccess: (data) => {
      console.log(data.data.data);
      const { manifestDetails, ...manifestValues } = data.data.data;
      setFormValues({ ...manifestValues, status: "Open" });
      setDetails(manifestDetails.map((d) => ({ ...d, status: statusLabel[d.status] })));
    },
    onError,
  });

  const editLoadManifestMutation = useMutation(editLoadManifest, {});

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;
    const { status, ...data } = getValues();
    const req = {
      ...data,
      manifestDetails: details.map((d) => ({ ...d, status: statusObj[d.status] })),
    };
    editLoadManifestMutation.mutate(
      { manifestNumber, req },
      {
        onSuccess: (data) => {
          navigate("/outbound/load-manifest", { state: data.data.message });
        },
        onError,
      }
    );
  };

  const onCancel = (data) => {
    setDetails(
      details.map((detail) => {
        if (detail.manifestDetailsId === data.manifestDetailsId) {
          detail.status = "Cancel";
        }
        return detail;
      })
    );
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };

  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...manifestColumns2,
  ];

  const caption = (
    <div>
      <span>Total Rows: {details.length}</span>
    </div>
  );
  const tableProps = { statusLabel:"LM", columns, rows: details, caption, onCancel, mode: "EDIT" };

  return (
    <InboundLayout lg={12}>
      {alert}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <LoadManifest {...headerProps} />
          </form>
          <MDBox sx={{ margin: "1rem 0" }} />
          <FormTitle title="Edit Load Manifest Detail" />
          <BasicTable2 {...tableProps} />
        </MDBox>
      </Card>
      <InboundSaveButton
        url="/outbound/load-manifest"
        label="Submit"
        onClick={onSubmit}
        disabled={isSubmitting}
      />
    </InboundLayout>
  );
}

export default EditLoadManifest;
